import axios from 'axios';
import { auth } from '../firebase/config';

const BACKEND_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

// Helper function to get auth headers
const getAuthHeaders = async () => {
    const user = auth.currentUser;
    if (user) {
        try {
            const token = await user.getIdToken();
            return {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            };
        } catch (error) {
            console.error('Error getting Firebase token:', error);
        }
    }
    return {
        'Content-Type': 'application/json'
    };
};

const ApiService = {
    // ==================== Authentication Logic ====================
    
    saveUser: async (user, token) => {
        console.log("Saving user: ", user);
        const userData = {
            id: user.uid,
            email: user.email,
            displayName: user.displayName,
            photoURL: user.photoURL,
            emailVerified: user.emailVerified,
            phoneNumber: user.phoneNumber,
            lastLoginAt: user.metadata?.lastSignInTime,
            providerData: user.providerData,
            multiFactor: user.multiFactor,
            createdAt: user.metadata?.creationTime,
            updatedAt: user.metadata?.lastSignInTime,
        };
        try {
            const response = await axios.post(`${BACKEND_URL}/api/users`, userData, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error saving user:', error);
            return null;
        }
    },

    getUser: async (uid, token) => {
        const response = await axios.get(`${BACKEND_URL}/api/users/${uid}`, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            }
        });
        return response.data;
    },

    getUserProfile: async (uid, token) => {
        const response = await axios.get(`${BACKEND_URL}/api/users/${uid}/profile`, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            }
        });
        return response.data;
    },

    saveUserProfile: async (uid, profile, token) => {
        const response = await axios.post(`${BACKEND_URL}/api/users/${uid}/profile`, profile, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            }
        });
        return response.data;
    },

    deleteUserAccount: async (uid, token) => {
        try {
            const response = await axios.delete(`${BACKEND_URL}/api/users/${uid}`, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error deleting user account:', error);
            throw error;
        }
    },

    sendVerificationCode: async (email, token) => {
        try {
            const response = await axios.post(`${BACKEND_URL}/api/sendEmailCode`,
                { email },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    }
                }
            );
            return response.data;
        } catch (error) {
            console.error('Error sending verification code:', error);
            return null;
        }
    },

    verifyCode: async (email, code, token) => {
        try {
            const response = await axios.post(`${BACKEND_URL}/api/verifyEmailCode`,
                { email, code },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    }
                }
            );
            return response.data;
        } catch (error) {
            console.error('Error verifying code:', error);
            return null;
        }
    },

    // ==================== Teacher Logic ====================

    createTeacher: async (teacher) => {
        const headers = await getAuthHeaders();
        const response = await axios.post(`${BACKEND_URL}/api/teacher`, teacher, { headers });
        return response.data;
    },

    getAllTeachers: async () => {
        const headers = await getAuthHeaders();
        const response = await axios.get(`${BACKEND_URL}/api/teachers`, { headers });
        return response.data;
    },

    getTeacherById: async (id) => {
        const headers = await getAuthHeaders();
        const response = await axios.get(`${BACKEND_URL}/api/teacher/${id}`, { headers });
        return response.data;
    },

    updateTeacher: async (id, teacher) => {
        const headers = await getAuthHeaders();
        const response = await axios.put(`${BACKEND_URL}/api/teacher/${id}`, teacher, { headers });
        return response.data;
    },

    deleteTeacher: async (id) => {
        const headers = await getAuthHeaders();
        const response = await axios.delete(`${BACKEND_URL}/api/teacher/${id}`, { headers });
        return response.data;
    },

    // ==================== Learning Session Logic ====================

    createLearningSession: async (session, token) => {
        console.log("Creating learning session: ", session);
        const response = await axios.post(`${BACKEND_URL}/api/learning_session`, session, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            }
        });
        return response.data;
    },

    getLearningSessionById: async (learningSessionId, token) => {
        const response = await axios.get(`${BACKEND_URL}/api/learning_session/${learningSessionId}`, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            }
        });
        return response.data;
    },

    getUserLearningSessions: async (userId, token) => {
        console.log("Getting user learning sessions: ", userId);
        const response = await axios.get(`${BACKEND_URL}/api/learning_sessions/${userId}`, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            }
        });
        return response.data;
    },

    updateLearningSession: async (learningSessionId, learningSession, token) => {
        const response = await axios.put(`${BACKEND_URL}/api/learning_session/${learningSessionId}`, learningSession, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            }
        });
        return response.data;
    },

    deleteLearningSession: async (learningSessionId, token) => {
        const response = await axios.delete(`${BACKEND_URL}/api/learning_session/${learningSessionId}`, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            }
        });
        return response.data;
    },

    getLearningSessionMessages: async (learningSessionId, token) => {
        const response = await axios.get(`${BACKEND_URL}/api/learning_session/${learningSessionId}/messages`, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            }
        });
        return response.data;
    },

    getLearningSessionSummary: async (userId, token) => {
        const response = await axios.get(`${BACKEND_URL}/api/learning_sessions_summary/${userId}`, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            }
        });
        return response.data;
    },

    // ==================== Realtime Session Logic ====================

    getRealtimeSessionKey: async (learningSessionId, teacherId, userId, token) => {
        const response = await axios.post(`${BACKEND_URL}/api/realtime_session_key`, {
            learningSessionId, 
            teacherId, 
            userId
        }, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            }
        });
        return response.data;
    },

    sendRealtimeMessage: async (message, token) => {
        const response = await axios.post(`${BACKEND_URL}/api/realtime_message`, message, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            }
        });
        return response.data;
    },

    // ==================== Attention Detection Logic ====================

    detectAttention: async (userId, learningSessionId, attentionPercentage, base64Image, saveImage = true, token) => {
        try {
            const response = await axios.post(`${BACKEND_URL}/api/attention`, {
                userId,
                learningSessionId,
                attentionPercentage,
                save_image: saveImage,
                base64_image: base64Image
            }, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error detecting attention:', error);
            return null;
        }
    },

    getAttentionBySession: async (sessionId, token) => {
        const response = await axios.get(`${BACKEND_URL}/api/attention/learning-session/${sessionId}`, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            }
        });
        return response.data;
    },

    getAttentionStats: async (sessionId, token) => {
        const response = await axios.get(`${BACKEND_URL}/api/attention/stats/${sessionId}`, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            }
        });
        return response.data;
    },

    // ==================== Chat Logic ====================

    getChatHistory: async (userId, teacherId, limit = 50, token) => {
        try {
            const response = await axios.post(`${BACKEND_URL}/api/chat/history`, {
                userId,
                teacherId,
                limit
            }, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error getting chat history:', error);
            return [];
        }
    },

    sendChatMessage: async (userId, teacherId, message, token) => {
        try {
            const response = await axios.post(`${BACKEND_URL}/api/chat/send`, {
                userId,
                teacherId,
                message
            }, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error sending chat message:', error);
            return null;
        }
    },

    // ==================== Invite Code Logic ====================

    generateInviteCode: async (userId, expiresIn = 24, token) => {
        try {
            const response = await axios.post(`${BACKEND_URL}/api/invite/generate`, {
                userId,
                expiresIn
            }, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error generating invite code:', error);
            return null;
        }
    },

    validateInviteCode: async (inviteCode, token) => {
        console.log("Validating invite code: ", inviteCode);
        const response = await axios.post(`${BACKEND_URL}/api/invite/validate`, {
            code: inviteCode
        }, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            }
        });
        return response.data;
    },

    getUserInviteCodes: async (userId, token) => {
        try {
            const response = await axios.get(`${BACKEND_URL}/api/users/${userId}/invites`, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error getting user invite codes:', error);
            return null;
        }
    },

    // ==================== Parent/Child Linking Logic ====================

    linkChildByInviteCode: async (parentId, inviteCode, token) => {
        try {
            const response = await axios.post(`${BACKEND_URL}/api/invite/use`, {
                usedByUserId: parentId,
                code: inviteCode
            }, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error linking child by invite code:', error);
            return null;
        }
    },

    getLinkedChildren: async (parentId, token) => {
        try {
            // First get parent's profile to get children IDs
            const profileResponse = await axios.get(`${BACKEND_URL}/api/users/${parentId}/profile`, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });

            const childrenIds = profileResponse.data?.children || [];
            
            if (childrenIds.length === 0) {
                return [];
            }

            // Fetch each child's profile and user data
            const childrenPromises = childrenIds.map(async (childId) => {
                try {
                    const [childProfile, childUser] = await Promise.all([
                        axios.get(`${BACKEND_URL}/api/users/${childId}/profile`, {
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`
                            }
                        }),
                        axios.get(`${BACKEND_URL}/api/users/${childId}`, {
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`
                            }
                        })
                    ]);

                    return {
                        userId: childId,
                        ...childProfile.data,
                        ...childUser.data,
                        displayName: `${childProfile.data.firstName || ''} ${childProfile.data.lastName || ''}`.trim() || childUser.data.displayName,
                        photoURL: childProfile.data.avatar || childUser.data.photoURL
                    };
                } catch (err) {
                    console.error(`Error fetching child ${childId}:`, err);
                    return null;
                }
            });

            const children = await Promise.all(childrenPromises);
            return children.filter(child => child !== null);
        } catch (error) {
            console.error('Error getting linked children:', error);
            return [];
        }
    },

    // ==================== Subscription Logic ====================

    getStripePublicKey: async () => {
        const response = await axios.get(`${BACKEND_URL}/api/stripe_public_key`, {
            headers: {
                'Content-Type': 'application/json'
            }
        });
        return response.data;
    },

    getPlanPrices: async () => {
        const response = await axios.get(`${BACKEND_URL}/api/prices`, {
            headers: {
                'Content-Type': 'application/json',
            }
        });
        return response.data;
    },

    createCheckoutSession: async (priceId, successUrl, cancelUrl, userId = null) => {
        const response = await axios.post(`${BACKEND_URL}/api/create-checkout-session`, {
            priceId,
            successUrl,
            cancelUrl,
            userId
        });
        return response.data;
    },

    createPaymentSheetParams: async (priceId, userId, email) => {
        console.log(priceId, userId, email);
        const response = await axios.post(`${BACKEND_URL}/api/create-payment-intent`, {
            priceId,
            userId,
            email
        });
        return response.data;
    },

    getSubscriptionStatus: async (userId, token) => {
        const response = await axios.get(`${BACKEND_URL}/api/subscription/status`, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            }
        });
        return response.data;
    },

    getSubscriptionDetails: async (userId, token) => {
        const response = await axios.get(`${BACKEND_URL}/api/subscription/details`, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            }
        });
        return response.data;
    },

    updateSubscription: async (userId, subscription, token) => {
        const response = await axios.put(`${BACKEND_URL}/api/subscription/update`, subscription, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            }
        });
        return response.data;
    },

    cancelSubscription: async (userId, token) => {
        const response = await axios.post(`${BACKEND_URL}/api/subscription/cancel`, {}, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            }
        });
        return response.data;
    },

    getSubscriptionHistory: async (userId, token) => {
        const response = await axios.get(`${BACKEND_URL}/api/subscription/history`, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            }
        });
        return response.data;
    },

    // ==================== Suggestions Logic ====================

    getSuggestions: async (type, query, token) => {
        try {
            const response = await axios.post(`${BACKEND_URL}/api/suggestions`, {
                type,
                query
            }, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching suggestions:', error);
            return [];
        }
    },

    // ==================== MFA Logic ====================

    unsetMultiFactor: async (uid, mfaType, token) => {
        const response = await axios.post(`${BACKEND_URL}/api/unsetMFA`, {
            uid,
            mfaType
        }, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            }
        });
        return response.data;
    },

    // ==================== Profile Logic ====================

    getProfile: async (userId) => {
        const headers = await getAuthHeaders();
        const response = await axios.get(`${BACKEND_URL}/api/users/${userId}/profile`, { headers });
        return response.data;
    },

    createOrUpdateProfile: async (userId, profileData) => {
        const headers = await getAuthHeaders();
        const response = await axios.post(`${BACKEND_URL}/api/users/${userId}/profile`, profileData, { headers });
        return response.data;
    },

    // ==================== Session Management Wrappers ====================

    createOrUpdateLearningSession: async (sessionData) => {
        const headers = await getAuthHeaders();
        const response = await axios.post(`${BACKEND_URL}/api/learning_session`, sessionData, { headers });
        return response.data;
    },

    getLearningSession: async (sessionId) => {
        const headers = await getAuthHeaders();
        const response = await axios.get(`${BACKEND_URL}/api/learning_session/${sessionId}`, { headers });
        return response.data;
    },

    getLearningSessionsByUser: async (userId) => {
        const headers = await getAuthHeaders();
        const response = await axios.get(`${BACKEND_URL}/api/learning_sessions/${userId}`, { headers });
        return response.data;
    },

    // ==================== Teacher Wrappers ====================

    getTeacher: async (teacherId) => {
        const headers = await getAuthHeaders();
        const response = await axios.get(`${BACKEND_URL}/api/teacher/${teacherId}`, { headers });
        return response.data;
    },

    // ==================== Chat Wrappers ====================

    sendMessage: async (userId, chatId, message) => {
        const headers = await getAuthHeaders();
        const response = await axios.post(`${BACKEND_URL}/api/chat/send`, {
            userId,
            chatId,
            message
        }, { headers });
        return response.data;
    },

    // ==================== Realtime Wrappers ====================

    getSessionKey: async (data) => {
        const headers = await getAuthHeaders();
        const response = await axios.post(`${BACKEND_URL}/api/realtime_session_key`, data, { headers });
        return response.data;
    },
};

export default ApiService;

